// src/main.tsx
import React from 'react'
import { createRoot } from 'react-dom/client'
import OmeloWidget from './OmeloWidget'

type WidgetConfig = {
  apiBaseUrl?: string
  partnerId?: string
  theme?: 'light' | 'dark'
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'
  language?: 'en' | 'hi' | 'hinglish'
  enableDiagnosticFlow?: boolean
  enableProductSearch?: boolean
  enableEmergencyDetection?: boolean
  maxQuestions?: number
  autoOpen?: boolean
  container?: string | HTMLElement
}

class OmeloWidgetManager {
  private root: any = null
  private container: HTMLElement | null = null
  private config: WidgetConfig = {}
  private widgetRef: any = null
  
  constructor(config: WidgetConfig = {}) {
    this.config = {
      apiBaseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000',
      partnerId: import.meta.env.VITE_DEFAULT_PARTNER_ID || 'demo-partner-001',  // Use valid partner ID
      theme: 'light',
      position: 'bottom-right',
      language: 'en',
      enableDiagnosticFlow: true,
      enableProductSearch: true,
      enableEmergencyDetection: true,
      maxQuestions: 4,
      autoOpen: false,
      ...config
    }
  }

  init(customConfig: WidgetConfig = {}): OmeloWidgetManager {
    // Merge configurations
    this.config = { ...this.config, ...customConfig }
    
    // Create or find container
    this.createContainer()
    
    // Mount the widget
    this.mount()
    
    // Auto-open if requested
    // if (this.config.autoOpen) {
    //   setTimeout(() => this.open(), 500)
    // }
    
    return this
  }

  private createContainer(): void {
    if (this.config.container) {
      // Use provided container
      if (typeof this.config.container === 'string') {
        this.container = document.querySelector(this.config.container)
        if (!this.container) {
          throw new Error(`Container "${this.config.container}" not found`)
        }
      } else {
        this.container = this.config.container
      }
    } else {
      // Create default container
      this.container = document.createElement('div')
      this.container.id = `omelo-widget-${Date.now()}`
      document.body.appendChild(this.container)
    }
  }

  private mount(): void {
    if (!this.container) return
    
    this.root = createRoot(this.container)
    this.root.render(
      <OmeloWidget 
        ref={(ref: any) => this.widgetRef = ref}
        {...this.config}
      />
    )
  }

  // Public API methods
  open(): void {
    if (this.widgetRef?.open) {
      this.widgetRef.open()
    }
  }

  close(): void {
    if (this.widgetRef?.close) {
      this.widgetRef.close()
    }
  }

  toggle(): void {
    if (this.widgetRef?.toggle) {
      this.widgetRef.toggle()
    }
  }

  sendMessage(message: string): void {
    if (this.widgetRef?.sendMessage) {
      this.widgetRef.sendMessage(message)
    }
  }

  clearChat(): void {
    if (this.widgetRef?.clearChat) {
      this.widgetRef.clearChat()
    }
  }

  updateConfig(newConfig: Partial<WidgetConfig>): void {
    this.config = { ...this.config, ...newConfig }
    this.destroy()
    this.init()
  }

  getConfig(): WidgetConfig {
    return { ...this.config }
  }

  isOpen(): boolean {
    return this.widgetRef?.isOpen() || false
  }

  destroy(): void {
    if (this.root) {
      this.root.unmount()
      this.root = null
    }
    
    if (this.container && this.container.parentNode && !this.config.container) {
      this.container.parentNode.removeChild(this.container)
    }
    
    this.container = null
    this.widgetRef = null
  }
}

// Auto-initialization from script tag
function autoInit(): void {
  const script = document.currentScript as HTMLScriptElement
  if (!script) return

  const config: WidgetConfig = {
    apiBaseUrl: script.dataset.apiUrl,
    partnerId: script.dataset.partnerId,
    theme: script.dataset.theme as any,
    position: script.dataset.position as any,
    language: script.dataset.language as any,
    enableDiagnosticFlow: script.dataset.diagnosticFlow !== 'false',
    enableProductSearch: script.dataset.productSearch !== 'false',
    enableEmergencyDetection: script.dataset.emergencyDetection !== 'false',
    maxQuestions: script.dataset.maxQuestions ? parseInt(script.dataset.maxQuestions) : undefined,
    autoOpen: script.dataset.autoOpen === 'true',
    container: script.dataset.container
  }

  // Remove undefined values
  Object.keys(config).forEach(key => {
    if (config[key as keyof WidgetConfig] === undefined) {
      delete config[key as keyof WidgetConfig]
    }
  })

  // Initialize widget when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new OmeloWidgetManager().init(config)
    })
  } else {
    new OmeloWidgetManager().init(config)
  }
}

// Global API
const OmeloWidgetAPI = {
  // Create new widget instance
  create: (config: WidgetConfig = {}) => new OmeloWidgetManager(config),
  
  // Initialize with configuration
  init: (config: WidgetConfig = {}) => new OmeloWidgetManager().init(config),
  
  // Quick initialization methods
  simple: (apiBaseUrl?: string, partnerId?: string) => 
    new OmeloWidgetManager().init({ apiBaseUrl, partnerId }),
    
  diagnostic: (config: WidgetConfig = {}) => 
    new OmeloWidgetManager().init({ 
      ...config, 
      enableDiagnosticFlow: true,
      enableEmergencyDetection: true 
    }),
    
  products: (config: WidgetConfig = {}) => 
    new OmeloWidgetManager().init({ 
      ...config, 
      enableProductSearch: true,
      enableDiagnosticFlow: false 
    }),
    
  emergency: (config: WidgetConfig = {}) => 
    new OmeloWidgetManager().init({ 
      ...config, 
      enableEmergencyDetection: true,
      enableDiagnosticFlow: true,
      maxQuestions: 2 
    }),

  // Utility methods
  version: '2.0.0',
  
  // Feature detection
  supportsFeature: (feature: string): boolean => {
    const supportedFeatures = [
      'diagnosticFlow',
      'productSearch', 
      'emergencyDetection',
      'multiLanguage',
      'themes',
      'positioning',
      'sessionManagement'
    ]
    return supportedFeatures.includes(feature)
  },

  // Get all supported languages
  getSupportedLanguages: () => [
    { code: 'en', name: 'English' },
    { code: 'hi', name: 'Hindi' },
    { code: 'hinglish', name: 'Hinglish' }
  ],

  // Get all available themes
  getSupportedThemes: () => [
    { code: 'light', name: 'Light Theme' },
    { code: 'dark', name: 'Dark Theme' }
  ],

  // Get all available positions
  getSupportedPositions: () => [
    { code: 'bottom-right', name: 'Bottom Right' },
    { code: 'bottom-left', name: 'Bottom Left' },
    { code: 'top-right', name: 'Top Right' },
    { code: 'top-left', name: 'Top Left' }
  ]
}

// Attach to window for global access
declare global {
  interface Window {
    OmeloWidget: typeof OmeloWidgetAPI
  }
}

window.OmeloWidget = OmeloWidgetAPI

// Auto-initialize if script tag has data attributes
autoInit()

export default OmeloWidgetAPI
export { OmeloWidgetManager, type WidgetConfig }


